<template>
  <div class="matched-session-page">
    <div class="content-container">
      <!-- 对方的会话（默认展开） -->
      <div class="session-item">
        <div class="session-header" @click="matchedExpanded = !matchedExpanded">
          <!-- 第一行：tag 标签 + 分享按钮 -->
          <div class="session-header-top">
            <van-tag type="primary" size="medium" class="session-tag">{{ matchTypeLabel }}</van-tag>
            <van-icon v-if="matchDetailId" name="share-o" size="18" color="#8A8A8A" style="cursor: pointer; margin-left: auto;" @click.stop="handleShareMatch" />
          </div>
          <!-- 第二行：userMsg 主内容 -->
          <div v-if="matchedSessionInfo.userMsg && !isMatchedSessionPrivate" class="session-user-msg">
            {{ $common.formatUserMsgPreview(matchedSessionInfo.userMsg) }}
          </div>
          <!-- 第三行：AI Summary -->
          <div v-if="matchedSessionInfo.aiSummary && !isMatchedSessionPrivate" class="session-ai-summary">
            <span class="session-ai-summary-label">{{ $t('aiChat_matchSummaryLabel') }}</span>
            <span class="session-ai-summary-text">{{ matchedSessionInfo.aiSummary }}</span>
          </div>
          <!-- 第四行：头像 + 用户名 + 时间 + 联系按钮 + 展开/折叠 -->
          <div class="session-header-bottom">
            <div class="session-user-info">
              <div v-if="matchedSessionInfo.fromUser" class="user-avatar avatar-clickable" @click.stop="goToUserProfile">
                <img v-if="matchedSessionInfo.fromUser.avatar" class="user-avatar-img" :src="$common.getFullImageUrl(matchedSessionInfo.fromUser.avatar, $config, 's')" />
                <div v-else class="user-avatar-placeholder">{{ (matchedSessionInfo.fromUser.userName || matchedSessionInfo.fromUser.email || $t('anonymous'))[0] }}</div>
              </div>
              <div class="user-name" v-if="matchedSessionInfo.fromUser">
                {{ matchedSessionInfo.fromUser.userName || matchedSessionInfo.fromUser.email }}
              </div>
              <div class="session-time" v-if="matchedSessionInfo.createdAt">{{ $common.formatSmartTime(matchedSessionInfo.createdAt, $i18n.locale) }}</div>
            </div>
            <div class="session-header-actions">
              <van-button type="primary" size="mini" round color="#E8543A" class="btn" @click.stop="onContactClick">
                <div style="display: flex;justify-content: center;align-items: center;gap: 4px;">
                  <van-icon name="chat-o" size="14" style="margin-bottom:1px;" />
                  <div>{{ $t('contact_now') }}</div>
                </div>
              </van-button>
              <van-icon v-if="!isMatchedSessionPrivate" :name="matchedExpanded ? 'arrow-up' : 'arrow-down'" size="16" color="#8A8A8A" />
            </div>
          </div>
        </div>
        <div v-show="matchedExpanded && !isMatchedSessionPrivate" class="chat-wrapper">
          <ChatMsgList v-if="matchedSessionInfo.fromUserId" ref="matchedChatMsgList" :chatSessionId="matchedChatSessionId" :toUserId="assistantUserId"
            :fromUserId="String(matchedSessionInfo.fromUserId)" :platformUserId="platformUserId" :pageSize="100"
            :maxHeight="chatListMaxHeight" :filterThink="1" @image-click="previewImage"
            :currentAvatarUrl="matchedSessionInfo.fromUser && matchedSessionInfo.fromUser.avatar"
            :currentUserName="matchedSessionInfo.fromUser && (matchedSessionInfo.fromUser.userName || matchedSessionInfo.fromUser.email)" />
        </div>
        <!-- 私密会话：整体一条提示，不展示对话内容 -->
        <div v-if="isMatchedSessionPrivate" class="private-session-hint">
          <van-icon name="lock" size="14" color="#8A8A8A" />
          <span>{{ $t('match_private_hint') }}</span>
        </div>
      </div>

      <!-- 小摇来分析（匹配理由） -->
      <div class="match-reasons-container" v-if="briefMatchReason || detailedMatchReason">
        <div class="match-reason-header">
          <span class="session-ai-summary-label">{{ $t('xiaoyao_analysis') }}</span>
        </div>
        <!-- 简要匹配理由 -->
        <div v-if="briefMatchReason" class="match-reason-brief-card">
          <span class="match-reason-brief-text">{{ briefMatchReason }}</span>
        </div>
        <!-- 详细匹配理由 -->
        <div v-if="detailedMatchReason" class="match-reason-detail-card">
          <div class="match-reason-detail-label">
            <van-icon name="description" size="13" color="#E8543A" style="display: flex; align-items: center;" />
            <span>{{ $t('detail') }}</span>
          </div>
          <div class="match-reason-detail-text" v-html="renderedDetailedReason"></div>
        </div>
      </div>

      <!-- 自己的会话（默认收起） -->
      <div class="session-item">
        <div class="session-header" @click="currentExpanded = !currentExpanded">
          <!-- 第一行：tag 标签 -->
          <div class="session-header-top">
            <van-tag v-if="isCurrentSessionMine" plain type="primary" size="medium" class="session-tag">{{ $t('yours') }}</van-tag>
          </div>
          <!-- 第二行：userMsg 主内容 -->
          <div v-if="currentSessionInfo.userMsg" class="session-user-msg">
            {{ $common.formatUserMsgPreview(currentSessionInfo.userMsg) }}
          </div>
          <!-- 第三行：AI Summary -->
          <div v-if="currentSessionInfo.aiSummary" class="session-ai-summary">
            <span class="session-ai-summary-label">{{ $t('aiChat_matchSummaryLabel') }}</span>
            <span class="session-ai-summary-text">{{ currentSessionInfo.aiSummary }}</span>
          </div>
          <!-- 第四行：头像 + 用户名 + 时间 + 展开/折叠 -->
          <div class="session-header-bottom">
            <div class="session-user-info">
              <div v-if="currentSessionInfo.fromUser" class="user-avatar">
                <img v-if="currentSessionInfo.fromUser.avatar" class="user-avatar-img" :src="$common.getFullImageUrl(currentSessionInfo.fromUser.avatar, $config, 's')" />
                <div v-else class="user-avatar-placeholder">{{ (currentSessionInfo.fromUser.userName || currentSessionInfo.fromUser.email || $t('anonymous'))[0] }}</div>
              </div>
              <div class="user-name" v-if="currentSessionInfo.fromUser">
                {{ currentSessionInfo.fromUser.userName || currentSessionInfo.fromUser.email }}
              </div>
              <div class="session-time" v-if="currentSessionInfo.createdAt">{{ $common.formatSmartTime(currentSessionInfo.createdAt, $i18n.locale) }}</div>
            </div>
            <div class="session-header-actions">
              <van-icon :name="currentExpanded ? 'arrow-up' : 'arrow-down'" size="16" color="#8A8A8A" />
            </div>
          </div>
        </div>
        <div v-show="currentExpanded" class="chat-wrapper">
          <ChatMsgList ref="currentChatMsgList" :chatSessionId="currentChatSessionId" :toUserId="assistantUserId"
            :fromUserId="String(currentSessionInfo.fromUserId || '')"
            :platformUserId="platformUserId" :pageSize="100" :maxHeight="chatListMaxHeight"
            :filterThink="1" @image-click="previewImage"
            :currentAvatarUrl="currentSessionInfo.fromUser && currentSessionInfo.fromUser.avatar"
            :currentUserName="currentSessionInfo.fromUser && (currentSessionInfo.fromUser.userName || currentSessionInfo.fromUser.email)" />
        </div>
      </div>
    </div>

    <!-- 联系前广告闸门（global，后端开关控制） -->
    <ContactAdGate :visible="showContactAdGate" :seconds="contactGateSeconds" @continue="onContactGateContinue" />

    <!-- 联系方式弹窗 -->
    <van-popup v-model="showContactPopup" position="bottom" round :style="{ height: '80%', width: '100%' }">
      <div class="contact-popup">
        <div class="contact-popup-header">
          <div class="app-hint-pill" @click="showAppDownloadPopup = true">
            <svg viewBox="0 0 24 24" fill="none" style="width: 11px; height: 11px;">
              <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" stroke-width="2"/>
              <line x1="10" y1="18" x2="14" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            {{ $t('hint_get_app') }}
          </div>
          <div class="contact-popup-title" v-if="matchedSessionInfo.fromUser">{{ $t('chat_with', {name: matchedSessionInfo.fromUser.userName || matchedSessionInfo.fromUser.email || 'User'}) }}</div>
          <div class="contact-popup-title" v-else>{{ $t('chat_with', {name: 'User'}) }}</div>
          <div style="display: flex; align-items: center; gap: 12px;">
            <van-button type="info" size="mini" @click="showMoreContacts = true">{{ $t('more') }}</van-button>
            <van-icon name="cross" size="20" color="#666" @click="showContactPopup = false" />
          </div>
        </div>
        <!-- ChatBox 组件 -->
        <div class="contact-chat-box">
          <ChatBox
            v-if="showContactPopup"
            :targetUserId="matchedSessionInfo.fromUserId"
            :targetUserAvatar="matchedSessionInfo.fromUser && matchedSessionInfo.fromUser.avatar"
            :targetUserName="matchedSessionInfo.fromUser && (matchedSessionInfo.fromUser.userName || matchedSessionInfo.fromUser.email)"
            :currentAvatarUrl="currentSessionInfo.fromUser && currentSessionInfo.fromUser.avatar"
            :currentUserName="currentSessionInfo.fromUser && (currentSessionInfo.fromUser.userName || currentSessionInfo.fromUser.email)"
            :chatMsgListStyle="{ minHeight: 'auto', maxHeight: 'none', backgroundColor: '#ECEAE6', padding: '12px' }"
            :showRequirementButtons="false" :showUserMsgBanner="false"
            chatBoxType=""
          />
        </div>
      </div>
    </van-popup>

    <!-- App 下载提示弹窗 -->
    <van-popup v-model="showAppDownloadPopup" round style="padding: 20px; width: 260px; text-align: center;">
      <svg viewBox="0 0 24 24" fill="none" style="width: 40px; height: 40px; color: #E8543A; margin: 0 auto 8px;">
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2"/>
        <path d="M12 18V12M12 12L9 9M12 12L15 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <div style="font-weight: bold; font-size: 16px; color: #1A1A1A; margin-bottom: 4px;">{{ $t('stay_connected') }}</div>
      <div style="font-size: 13px; color: #8A8A8A; margin-bottom: 16px;">{{ $t('download_app_desc') }}</div>
      <div style="display: flex; flex-direction: column; gap: 8px;">
        <a :href="appStoreUrl" target="_blank" class="app-download-btn app-store">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 16px; height: 16px;">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.21-1.98 1.07-3.11-1.05.05-2.31.7-3.06 1.53-.68.74-1.26 1.93-1.1 3.07 1.18.09 2.38-.59 3.09-1.49z"/>
          </svg>
          {{ $t('app_store') }}
        </a>
        <a :href="googlePlayUrl" target="_blank" class="app-download-btn google-play">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 16px; height: 16px;">
            <path d="M3 20.5V3.5c0-.74.8-1.21 1.45-.88l15.84 8.5c.66.35.66 1.31 0 1.66L4.45 21.38c-.65.33-1.45-.14-1.45-.88z"/>
          </svg>
          {{ $t('google_play') }}
        </a>
        <a :href="apkUrl" target="_blank" class="app-download-btn apk" download>
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 16px; height: 16px;">
            <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/>
          </svg>
          {{ $t('direct_apk') }}
        </a>
      </div>
    </van-popup>

    <!-- 更多联系方式弹窗 -->
    <ContactMethods :visible.sync="showMoreContacts" :contactInfo="currentContactInfo"
      :targetUserId="matchedSessionInfo.fromUserId || ''"
      :userName="matchedSessionInfo.fromUser && (matchedSessionInfo.fromUser.userName || matchedSessionInfo.fromUser.email) || ''"
      :userAvatar="matchedSessionInfo.fromUser && matchedSessionInfo.fromUser.avatar || ''"
      :userBio="matchedSessionInfo.fromUser && matchedSessionInfo.fromUser.bio || ''" />

    <!-- 用户详情弹窗（点击头像） -->
    <ContactMethods :visible.sync="showUserDetailPopup"
      :targetUserId="matchedSessionInfo.fromUserId || ''"
      :userName="matchedSessionInfo.fromUser && (matchedSessionInfo.fromUser.userName || matchedSessionInfo.fromUser.email) || ''"
      :userAvatar="matchedSessionInfo.fromUser && matchedSessionInfo.fromUser.avatar || ''"
      :userBio="matchedSessionInfo.fromUser && matchedSessionInfo.fromUser.bio || ''"
      :showBlockActions="false" />
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import ChatMsgList from './ChatMsgList.vue'
import ChatBox from './ChatBox.vue'
import ContactMethods from './ContactMethods.vue'
import ContactAdGate from './ContactAdGate.vue'
import contactAdGateMixin from '@/mixins/contactAdGate.js'
import { NavBar, Loading, Empty, Button, Icon, Toast, ImagePreview, Popup, Tag } from 'vant'
import MarkdownIt from 'markdown-it'

Vue.use(ImagePreview)

// 初始化 markdown-it
const md = new MarkdownIt({
  breaks: true, // 支持换行符
  linkify: true, // 自动识别链接
  typographer: true // 启用印刷替换
})

export default {
  name: 'MatchedSession',
  components: {
    [NavBar.name]: NavBar,
    [Loading.name]: Loading,
    [Empty.name]: Empty,
    [Button.name]: Button,
    [Icon.name]: Icon,
    [Toast.name]: Toast,
    [Popup.name]: Popup,
    [Tag.name]: Tag,
    ChatMsgList,
    ChatBox,
    ContactMethods,
    ContactAdGate
  },
  mixins: [contactAdGateMixin],
  data() {
    return {
      platformUserId: '20210802',
      assistantUserId: '20210802', // 助手用户ID
      currentChatSessionId: '',
      matchedChatSessionId: '',
      currentSessionInfo: {}, // 当前会话详细信息
      matchedSessionInfo: {}, // 匹配会话详细信息
      currentMatchType: '', // 当前匹配类型
      briefMatchReason: '',
      detailedMatchReason: '',
      matchDetailId: '',

      // 收起展开状态
      matchedExpanded: true,  // 对方的会话默认展开
      currentExpanded: false, // 自己的会话默认收起

      // 聊天列表高度限制
      chatListMaxHeight: '400px', // 聊天列表最大高度

      // 联系方式弹窗
      showContactPopup: false,
      showMoreContacts: false, // 更多联系方式弹窗
      showUserDetailPopup: false, // 用户详情弹窗
      // 联系前广告闸门状态在 contactAdGate mixin 里（showContactAdGate/contactGateSeconds）
      defaultAvatar: 'https://fastly.jsdelivr.net/npm/@vant/assets/cat.jpeg', // 默认头像
      // App 下载提示弹窗
      showAppDownloadPopup: false,
      appStoreUrl: 'https://apps.apple.com/us/app/octopie-ai/id6760101997',
      googlePlayUrl: 'https://play.google.com/store/apps/details?id=ai.octopie.client',
      apkUrl: 'https://download.octosay.ai/octopie_ai.apk'
    }
  },
  computed: {
    // 判断右侧session是否属于当前登录用户
    isCurrentSessionMine() {
      const currentUserId = this.$store.state.user && this.$store.state.user.userId
      return currentUserId && String(this.currentSessionInfo.fromUserId) === String(currentUserId)
    },
    // 匹配方的会话已设为私有（public != 1）且不属于当前登录用户：
    // 后端会把 userMsg/aiSummary/消息内容全部置空，前端整体展示一条私密提示即可
    isMatchedSessionPrivate() {
      if (!this.matchedSessionInfo || !this.matchedSessionInfo.fromUserId) return false
      const currentUserId = this.$store.state.user && this.$store.state.user.userId
      if (currentUserId && String(this.matchedSessionInfo.fromUserId) === String(currentUserId)) return false
      return String(this.matchedSessionInfo.public) !== '1'
    },
    // 匹配类型标签
    matchTypeLabel() {
      const matchType = this.matchedSessionInfo.matchType || this.currentMatchType
      if (matchType === 'interested') return this.$t('label_worth_a_look')
      return this.$t('label_perfect_match')
    },
    // 渲染后的详细匹配理由 Markdown 内容
    renderedDetailedReason() {
      if (!this.detailedMatchReason) return ''
      try {
        return md.render(this.detailedMatchReason)
      } catch (error) {
        console.error('[MatchedSession] Markdown rendering error:', error)
        return this.detailedMatchReason
      }
    },
    // 整合当前联系人的所有联系方式信息
    currentContactInfo() {
      return {
        email: this.matchedSessionInfo.email || '',
        mobile: (this.matchedSessionInfo.fromUser && this.matchedSessionInfo.fromUser.mobile) || '',
        whatsapp: this.matchedSessionInfo.whatsapp || '',
        telegram: (this.matchedSessionInfo.fromUser && this.matchedSessionInfo.fromUser.telegram) || '',
        wechat: (this.matchedSessionInfo.fromUser && this.matchedSessionInfo.fromUser.wechat) || '',
        discord: (this.matchedSessionInfo.fromUser && this.matchedSessionInfo.fromUser.discord) || '',
        line: this.matchedSessionInfo.line || '',
        signal: (this.matchedSessionInfo.fromUser && this.matchedSessionInfo.fromUser.signal) || ''
      }
    }
  },
  created() {
    // 从路由参数获取
    this.currentChatSessionId = this.$route.query.currentChatSessionId || ''
    this.matchedChatSessionId = this.$route.query.matchedChatSessionId || ''
    const matchDetailId = this.$route.query.matchDetailId || ''
    this.matchDetailId = matchDetailId

    // 优先从 sessionStorage 缓存读取 matchReason
    const cacheKey = `match_reason_${this.currentChatSessionId}_${this.matchedChatSessionId}`
    let matchReason = ''
    try {
      matchReason = sessionStorage.getItem(cacheKey) || ''
      // 读取后清除缓存，避免内存泄漏
      if (matchReason) {
        sessionStorage.removeItem(cacheKey)
      }
    } catch (e) {
      console.warn('[MatchedSession] Failed to read matchReason from cache:', e)
    }

    if (matchReason) {
      this.detailedMatchReason = matchReason
    }

    console.log('[MatchedSession] Created with:', {
      currentChatSessionId: this.currentChatSessionId,
      matchedChatSessionId: this.matchedChatSessionId,
      matchDetailId: matchDetailId,
      matchReason: matchReason ? 'from cache' : 'not provided'
    })

    if (this.currentChatSessionId) {
      // 先获取会话信息，等待完成后再获取聊天消息
      this.fetchSessionInfo().then(() => {
        this.fetchChatMessages()
      })
      // 如果没有 matchReason，尝试通过 matchDetailId 或 sessionIds 获取
      if (!matchReason) {
        this.fetchMatchDetail(matchDetailId)
      }
    } else {
      this.error = this.$t('error_missing_session_params')
    }
  },
  mounted() {
    // 组件挂载后，不在这里刷新聊天消息，改为在 fetchSessionInfo 完成后调用
  },
  methods: {
    // 点击发帖人头像 → 跳转对方 profile（自己则跳个人中心）
    goToUserProfile () {
      var info = this.matchedSessionInfo
      var uid = info && info.fromUserId
      if (!uid) return
      var myId = this.$root.$common.getUserId()
      if (String(uid) === String(myId)) {
        this.$router.push('/personal-center')
        return
      }
      this.$router.push({ path: '/personal-center', query: { userId: String(uid) } })
    },
    async handleShareMatch() {
      const { shareMatch } = await import('@/services/share')
      const currentUserId = this.$store.state.user && this.$store.state.user.userId
      await shareMatch({
        matchDetailId: this.matchDetailId,
        matchLevel: this.currentMatchType || 'matched',
        briefMatchReason: this.briefMatchReason,
        userId: currentUserId || ''
      }, this.$i18n)
    },
    /**
     * 点击 Contact Now 按钮，检查登录状态
     */
    onContactClick() {
      if (!this.$store.state.alreadyLogin) {
        this.$store.state.needLogin = true
        return
      }
      // 联系前广告闸门：由后端 web.pages.matches.enabled 动态控制，默认关（逻辑在 contactAdGate mixin）
      if (this.tryContactGate()) return
      this.showContactPopup = true
    },
    // 闸门放行后打开联系弹窗（mixin 钩子）
    gateContinue() {
      this.showContactPopup = true
    },
    /**
     * 获取两个 session 的详细信息
     */
    async fetchSessionInfo() {
      // 安全检查：必须登录才能获取会话信息
      if (!this.$store.state.alreadyLogin) {
        return
      }
      try {
        // 并行获取两个 session 的信息
        const [currentResp, matchedResp] = await Promise.all([
          axios.post('/api/chat/get_chat_session', { chatSessionId: this.currentChatSessionId }),
          axios.post('/api/chat/get_chat_session', { chatSessionId: this.matchedChatSessionId })
        ])

        console.log('[MatchedSession] Session info fetched:', {
          current: currentResp.data,
          matched: matchedResp.data
        })

        if (currentResp.data && currentResp.data.code === 0 && currentResp.data.data.chatSession) {
          this.currentSessionInfo = currentResp.data.data.chatSession
        }

        if (matchedResp.data && matchedResp.data.code === 0 && matchedResp.data.data.chatSession) {
          this.matchedSessionInfo = matchedResp.data.data.chatSession
        }
      } catch (error) {
        console.error('[MatchedSession] Failed to fetch session info:', error)
        // 获取会话信息失败不影响主要功能，只记录错误
      }
    },

    /**
     * 刷新两个 session 的聊天消息
     */
    async fetchChatMessages() {
      console.log('in fetchChatMessages')
      try {
        // 调用 ChatMsgList 的 refresh 方法来加载消息
        if (this.$refs.currentChatMsgList) {
          console.log('currentChatMsgList.refresh()')
          this.$refs.currentChatMsgList.refresh()
        }
        if (this.$refs.matchedChatMsgList) {
          console.log('matchedChatMsgList.refresh()')
          this.$refs.matchedChatMsgList.refresh()
        }
      } catch (error) {
        console.error('[MatchedSession] Failed to fetch chat messages:', error)
        // 获取聊天消息失败不影响主要功能，只记录错误
      }
    },

    /**
     * 从 match_detail 表获取匹配详情
     * @param {string} matchDetailId - 匹配详情ID（优先使用）
     */
    async fetchMatchDetail(matchDetailId = '') {
      try {
        let response
        if (matchDetailId) {
          // 优先通过 matchDetailId 查询
          response = await axios.post('/api/chat/get_match_detail_by_id', {
            matchDetailId: matchDetailId
          })
          console.log('[MatchedSession] get_match_detail_by_id response:', response.data)
        } else if (this.currentChatSessionId && this.matchedChatSessionId) {
          // 兼容旧方式：通过 sessionIds 查询
          response = await axios.post('/api/chat/get_match_detail', {
            sourceChatSessionId: this.currentChatSessionId,
            matchedChatSessionId: this.matchedChatSessionId
          })
          console.log('[MatchedSession] get_match_detail response:', response.data)
        } else {
          return
        }

        if (response.data && response.data.code === 0 && response.data.data && response.data.data.matchDetail) {
          const matchDetail = response.data.data.matchDetail
          // 更新 matchedChatSessionId（如果是从 matchDetailId 查询的）
          if (matchDetail.matchedChatSessionId && !this.matchedChatSessionId) {
            this.matchedChatSessionId = matchDetail.matchedChatSessionId
          }
          // 保存匹配类型
          if (matchDetail.matchLevel) {
            this.currentMatchType = matchDetail.matchLevel
          }
          // 分别保存 briefMatchReason 和 detailedMatchReason
          if (matchDetail.briefMatchReason) {
            this.briefMatchReason = matchDetail.briefMatchReason
          }
          if (matchDetail.detailedMatchReason) {
            this.detailedMatchReason = matchDetail.detailedMatchReason
          }
          if (matchDetail.briefMatchReason || matchDetail.detailedMatchReason) {
            console.log('[MatchedSession] Got matchReason from match_detail table')
          }
        }
      } catch (error) {
        console.error('[MatchedSession] Failed to fetch match detail:', error)
      }
    },

    // 预览图片
    previewImage(url) {
      ImagePreview({
        images: [url],
        closeable: true,
        closeIcon: 'cross',
        closeIconPosition: 'top-right',
        closeOnClickOverlay: true,
        showIndex: false
      });
    }
  }
}
</script>

<style scoped>
.matched-session-page {
  height: 100vh;
  background: #F7F5F2;
  display: flex;
  flex-direction: column;
}

.content-container {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}

.match-reasons-container {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
}

.match-reason-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}

.match-reason-header-title {
  font-size: 14px;
  font-weight: 600;
  color: #E8543A;
}

.match-reason-brief-card {
  padding: 8px 10px;
  background: #FEF0ED;
  border-radius: 8px;
  border: 0.5px solid rgba(255, 151, 106, 0.3);
  display: flex;
  align-items: flex-start;
  gap: 6px;
  text-align: left;
}

.match-reason-brief-icon {
  flex-shrink: 0;
  font-size: 12px;
  line-height: 1.5;
}

.match-reason-brief-text {
  font-size: 12px;
  color: #4A4A4A;
  line-height: 1.5;
  word-break: break-word;
}

.match-reason-detail-card {
  margin-top: 12px;
  padding: 8px 10px;
  background: #FFF8F6;
  border-radius: 8px;
  border: 0.5px solid rgba(255, 151, 106, 0.15);
  text-align: left;
}

.match-reason-detail-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #E8543A;
  margin-bottom: 6px;
}

.match-reason-detail-text {
  font-size: 12px;
  line-height: 1.6;
  color: #4A4A4A;
}

.session-item {
  margin-bottom: 12px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  min-width: 0;
}

.chat-wrapper {
  padding: 0 15px 15px;
}

/* 私密会话整体提示 */
.private-session-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0 15px 15px;
  padding: 16px 12px;
  border-top: 1px solid #F0EEEB;
  color: #8A8A8A;
  font-size: 13px;
}
.private-session-hint span {
  line-height: 1.4;
}

.session-header {
  cursor: pointer;
}

.session-header-top {
  padding: 12px 15px 8px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.session-header-top .session-tag {
  /* tag样式 */
}

.session-user-msg {
  padding: 0 15px 6px;
  font-size: 14px;
  color: #4A4A4A;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 6px;
  text-align: left;
}

.session-ai-summary {
  padding: 0 15px 8px;
  text-align: left;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 8px;
  line-height: 1.5;
}

.session-header-bottom {
  padding: 8px 15px;
  border-top: 1px solid #E0DDD8;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.session-ai-summary-label {
  font-size: 11px;
  color: #E8543A;
  background: #FEF0ED;
  padding: 1px 6px;
  border-radius: 4px;
  flex-shrink: 0;
  line-height: 1.4;
}

.session-ai-summary-text {
  font-size: 12px;
  color: #929292;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.session-user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.session-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 8px;
}

.session-header .user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
}

.session-header .user-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.session-header .user-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #E05252;
  color: white;
  font-size: 12px;
  font-weight: 600;
}

.session-header .avatar-clickable {
  cursor: pointer;
}

.session-header .user-name {
  font-size: 14px;
  color: #1A1A1A;
  font-weight: 600;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-header .session-time {
  font-size: 12px;
  color: #8A8A8A;
  flex-shrink: 0;
}

.session-header .btn {
  /* 按钮在 session-header-actions 内 */
}

/* 立即联系按钮 */
.session-header-actions .van-button {
  flex-shrink: 0;
}

/* 联系方式弹窗 */
.contact-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.contact-popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.contact-popup-title {
  font-size: 16px;
  font-weight: 600;
  color: #1A1A1A;
  flex: 1;
}

.contact-chat-box {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.app-hint-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: linear-gradient(135deg, #E8543A, #C93E1A);
  color: #ffffff;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.contact-popup-header .app-hint-pill {
  padding: 5px 10px;
}

.app-hint-pill:hover {
  opacity: 0.9;
  transform: scale(1.02);
}

.app-download-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  text-decoration: none;
  transition: all 0.2s ease;
}

.app-store {
  background: #1A1A1A;
}

.app-store:hover {
  background: #1A1A1A;
}

.google-play {
  background: #3DAECF;
}

.google-play:hover {
  background: #2A8FAD;
}

.apk {
  background: #E8543A;
}

.apk:hover {
  background: #C93E1A;
}

.contact-chat-box>>>.van-popup,
.contact-chat-box>>>.van-popover {
  z-index: 99999 !important;
}

/* 聊天包装器 */
.chat-wrapper {
  /* 不再设置固定高度，由 ChatMsgList 内部的 maxHeight 控制 */
}

/* Markdown 样式 */
.match-reason-detail-text :deep(h2) {
  font-size: 16px;
  font-weight: 600;
  color: #1A1A1A;
  margin: 16px 0 8px 0;
  padding-bottom: 4px;
  border-bottom: 1px solid #E0DDD8;
}

.match-reason-detail-text :deep(h3) {
  font-size: 15px;
  font-weight: 600;
  color: #1A1A1A;
  margin: 12px 0 6px 0;
}

.match-reason-detail-text :deep(p) {
  margin: 8px 0;
}

.match-reason-detail-text :deep(ul),
.match-reason-detail-text :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
}

.match-reason-detail-text :deep(li) {
  margin: 4px 0;
}

.match-reason-detail-text :deep(strong),
.match-reason-detail-text :deep(b) {
  color: #E8543A;
  font-weight: 600;
}

.match-reason-detail-text :deep(blockquote) {
  margin: 12px 0;
  padding: 8px 12px;
  background: #FAFAF8;
  border-left: 3px solid #E8543A;
  color: #4A4A4A;
  font-size: 13px;
  border-radius: 4px;
}

.match-reason-detail-text :deep(code) {
  padding: 2px 6px;
  background: #FAFAF8;
  border-radius: 3px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 12px;
  color: #E8543A;
}

.match-reason-detail-text :deep(pre) {
  margin: 12px 0;
  padding: 12px;
  background: #1A1A1A;
  color: #8A8A8A;
  border-radius: 6px;
  overflow-x: auto;
}

.match-reason-detail-text :deep(pre code) {
  padding: 0;
  background: transparent;
  color: inherit;
}

.match-reason-detail-text :deep(h2:first-child),
.match-reason-detail-text :deep(h3:first-child) {
  margin-top: 0;
}

.note-box {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 12px;
  background: #FEF9E7;
  border-left: 3px solid #E8543A;
  border-radius: 4px;
  font-size: 12px;
  color: #E8543A;
  line-height: 1.6;
}

.note-box .van-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
</style>



// WEBPACK FOOTER //
// src/components/MatchedSession.vue